import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { TextAreaFieldComponent } from 'src/app/components/form/textarea-field/textarea-field.component';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { OptionService } from 'src/app/services/api/option.service';
import { ChecklistCacheService, ChecklistOption } from 'src/app/services/checklist-cache.service';
import { HttpParams } from '@angular/common/http';
import { Subject, takeUntil, forkJoin, of } from 'rxjs';
import { SubmittableComponent } from '../projects-accordion/projects-accordion.component';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { FormService } from 'src/app/services/form.service';
import { catchError, concatMap, tap } from 'rxjs/operators';

const params = new HttpParams().set('size', '9999');
@Component({
  selector: 'projects-working-list',
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent,
    TextAreaFieldComponent
],
  templateUrl: './projects-working-list.component.html',
  styleUrl: './projects-working-list.component.scss'
})
export class ProjectsWorkingListComponent implements OnInit, OnDestroy, OnChanges, SubmittableComponent {
  @Input() projectId?: string;
  @Input() jurisdictionId?: string | number | null;
  @Output() onWorkingListUpdated = new EventEmitter<any>();
  
  workingListItems: any[] = [];
  nextId: number = 1;

  form!: FormGroup;
  
  platformOptions: { key: string; value: string }[] = [];
  
  checklistOptions: ChecklistOption[] = [];

  workingItemForm: FormGroup;

  private destroy$ = new Subject<void>();
  private isInitialized = false;
  
  itemIndexToDelete: number | null = null;

  private pendingRestoreValues: Map<number, { name: string; platform: any; checklist: any; time: string }> = new Map();
  
  constructor(
    private fb: FormBuilder,
    private projectsService: ProjectsService,
    private optionsService: OptionService,
    private checklistCacheService: ChecklistCacheService,
    private alertNotificationService: AlertNotificationService,
    private formService: FormService,
    private confirmationDialogService: ConfirmationDialogService
  ) {

    this.form = this.fb.group({
      loginData: [''],
      notes: ['']
    });
    
    this.workingItemForm = this.fb.group({
      name: ['', Validators.required],
      product: [{ value: '', disabled: true }, Validators.required],
      platform: ['Desktop', Validators.required],
      checklist: ['', Validators.required],
      time: ['--:--']
    });
  }

  ngOnInit(): void {
    if (this.projectId) {
      this.loadWorkingListData(this.jurisdictionId);
      this.optionsService.platforms()
        .pipe(takeUntil(this.destroy$))
        .subscribe((options:any) => {
          this.platformOptions = options.data.platforms.map((platform:any) => ({ key: platform.platformId, value: platform.name }) );
        });
      
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jurisdictionId'] && !changes['jurisdictionId'].firstChange && this.projectId) {
      this.loadWorkingListData(this.jurisdictionId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reloadWithJurisdiction(jurisdictionId?: string | number | null): void {
    this.loadWorkingListData(jurisdictionId);
  }

  loadAllChecklists(): void {
    this.checklistCacheService.getAllChecklists()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => {
          this.checklistOptions = options;
        },
        error: (error) => {
          console.error('Error loading all checklists:', error);
          this.checklistOptions = [];
        }
      });
  }

  loadWorkingListData(jurisdictionId?: string | number | null): void {
    if (!this.projectId) return; 

      this.isInitialized = false;
      this.getLoginDataInput().setValue('');
      this.getNotesInput().setValue('');
      
      let params = new HttpParams();
      if (jurisdictionId !== null && jurisdictionId !== undefined && jurisdictionId !== '') {
        params = params.set('jurisdictionId', `${jurisdictionId}`);
      }

      this.projectsService.listProjectsWorkingLists(Number(this.projectId), { params })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
        next: (response) => {
          this.workingListItems = response.data.projectWorkinglists;
          
          if (this.workingListItems.length > 0) {
            this.nextId = 1;
          }
          
          this.workingListItems.forEach(item => {
            this.initFormControls(item);
            const saved = this.pendingRestoreValues.get(item.projectWorkinglistId);
            if (saved) {
              item.nameControl.setValue(saved.name);
              item.platformControl.setValue(saved.platform);
              item.checklistControl.setValue(saved.checklist);
              item.timeControl.setValue(saved.time);
            }
          });
          this.pendingRestoreValues.clear();

          this.loadAllChecklists();

          this.isInitialized = true;
          
          this.emitChanges();
        },
        error: (error) => {
          console.error('Error loading working list data:', error);
        }
      });
  }

  createFormControl(item: any, field: string): FormControl {
    const control = new FormControl(item[field as keyof any] || '', Validators.required);
    (item as any)[`${field}Control`] = control;
    if (!item.hasOwnProperty('isCreatingChecklist')) {
      item.isCreatingChecklist = false;
    }
    return control;
  }

  initFormControls(item: any): void {
    item.nameControl = new FormControl(item?.name || '', Validators.required);
    item.productControl = new FormControl(item?.itemProduct?.name || '', Validators.required);
    item.platformControl = new FormControl(item?.platform?.platformId || '', Validators.required);
    item.checklistControl = new FormControl(item?.checklist?.checklistId || '', Validators.required);
    const formattedTime = this.convertMinutesToHHMM(item?.timeElapsed);
    item.timeControl = new FormControl(formattedTime );
    item.isCreatingChecklist = false;
  }

  createChecklist(index: number): void {
    if (!this.projectId) {
      this.alertNotificationService.error('Project ID is missing');
      return;
    }

    const item = this.workingListItems[index];
    
    if (!item || !item.projectWorkinglistId) {
      this.alertNotificationService.error('Unable to create checklist: missing required information');
      return;
    }


    const isValid = this.validateItem(item);
    
    if (!isValid) {
      this.alertNotificationService.error('Please fill in all required fields correctly');
      return;
    }

    item.isCreatingChecklist = true;
    
    const itemData = this.prepareItemDataForSave(item);
    
    this.projectsService.updateProjectsWorkingLists(
      Number(this.projectId),
      item.projectWorkinglistId,
      itemData
    )
    .pipe(
      tap((putResponse) => {
        if (!putResponse || putResponse.success === false) {
          throw new Error('PUT request did not return a successful response');
        }
      }),
      concatMap(() => {
        return this.projectsService.createProjectChecklistFromWorkingList(
          Number(this.projectId),
          item.projectWorkinglistId,
          {}
        );
      }),
      takeUntil(this.destroy$)
    )
    .subscribe({
      next: (response) => {
        item.isCreatingChecklist = false;
        this.alertNotificationService.success('Item saved and checklist created successfully');
        this.workingListItems.forEach((wItem, idx) => {
          if (idx !== index) {
            this.pendingRestoreValues.set(wItem.projectWorkinglistId, {
              name: this.getItemNameControl(wItem).value,
              platform: this.getItemPlatformControl(wItem).value,
              checklist: this.getItemChecklistControl(wItem).value,
              time: this.getItemTimeControl(wItem).value,
            });
          }
        });
        this.loadWorkingListData(this.jurisdictionId);
      },
      error: (error) => {
        item.isCreatingChecklist = false;
        console.error('Error in create checklist flow:', error);
        
        if (error?.error?.errors && Array.isArray(error.error.errors)) {
          this.mapBackendErrorsToItemControls(item, error.error.errors);
          this.alertNotificationService.error('Validation errors found. Please check the form fields.');
        } else {
          this.alertNotificationService.error('Failed to save item or create checklist. Please try again.');
        }
      }
    });
  }

  private validateItem(item: any): boolean {
    const nameControl = this.getItemNameControl(item);
    const platformControl = this.getItemPlatformControl(item);
    const checklistControl = this.getItemChecklistControl(item);
    nameControl.markAsTouched();
    platformControl.markAsTouched();
    checklistControl.markAsTouched();
    
    return nameControl.valid && platformControl.valid && checklistControl.valid;
  }

  private prepareItemDataForSave(item: any): any {
    const platformId = this.getItemPlatformControl(item).value
      ?? item.platform?.platformId
      ?? item.platform;

    const checklistId = this.getItemChecklistControl(item).value
      ?? item.checklist?.checklistId
      ?? item.checklist;

    const timeElapsed = this.convertHHMMToMinutes(this.getItemTimeControl(item).value)
      || item.timeElapsed
      || 0;

    return {
      name: this.getItemNameControl(item).value || item.name,
      platformId,
      checklistId,
      timeElapsed
    };
  }

  private mapBackendErrorsToItemControls(item: any, errors: any[]): void {
    this.resetItemErrors(item);
    
    errors.forEach((error: any) => {
      if (!error.field) return;
      
      const fieldName = error.field;
      const errorMessage = error.defaultMessage || 'Invalid value';
    
      switch (fieldName) {
        case 'name':
          this.getItemNameControl(item).setErrors({ backendError: errorMessage });
          this.getItemNameControl(item).markAsTouched();
          break;
        case 'platformId':
        case 'platform':
          this.getItemPlatformControl(item).setErrors({ backendError: errorMessage });
          this.getItemPlatformControl(item).markAsTouched();
          break;
        case 'checklistId':
        case 'checklist':
          this.getItemChecklistControl(item).setErrors({ backendError: errorMessage });
          this.getItemChecklistControl(item).markAsTouched();
          break;
        case 'timeElapsed':
        case 'time':
          this.getItemTimeControl(item).setErrors({ backendError: errorMessage });
          this.getItemTimeControl(item).markAsTouched();
          break;
        default:
          console.warn(`Unknown field in backend errors: ${fieldName}`);
      }
    });
  }

  private resetItemErrors(item: any): void {
    const controls = [
      this.getItemNameControl(item),
      this.getItemPlatformControl(item),
      this.getItemChecklistControl(item),
      this.getItemTimeControl(item)
    ];
    
    controls.forEach(control => {
      if (control.hasError('backendError')) {
        const errors = { ...control.errors };
        delete errors['backendError'];
        
        if (Object.keys(errors).length === 0) {
          control.setErrors(null);
        } else {
          control.setErrors(errors);
        }
      }
    });
  }

  removeItem(index: number): void {
    this.itemIndexToDelete = index;
    const itemToDelete = this.workingListItems[index];
    const message = 'Are you sure you want to delete this item?';
    
    this.confirmationDialogService.open(
      message,
      () => {
        if (this.itemIndexToDelete !== null && this.itemIndexToDelete >= 0) {
          const item = this.workingListItems[this.itemIndexToDelete];
          
          if (!this.projectId || !item.projectWorkinglistId) {
            this.alertNotificationService.error('Unable to delete item: missing required information');
            this.itemIndexToDelete = null;
            return;
          }

          this.projectsService.deleteProjectsWorkingLists(Number(this.projectId), item.projectWorkinglistId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                this.workingListItems.splice(this.itemIndexToDelete!, 1);
                this.emitChanges();
                this.alertNotificationService.success('Item deleted successfully');
                this.itemIndexToDelete = null;
              },
              error: (error) => {
                console.error('Error deleting working list item:', error);
                this.alertNotificationService.error('Failed to delete item. Please try again.');
                this.itemIndexToDelete = null;
              }
            });
        }
      },
      () => {
        this.itemIndexToDelete = null;
      }
    );
  }

  getChecklistOptionsForItem(item: any): ChecklistOption[] {
    const currentChecklistId = item?.checklist?.checklistId;
    const itemJurisdictionId = item?.projectItem?.proposalItem?.jurisdiction?.jurisdictionId;

    return this.checklistOptions.filter(option =>
      (currentChecklistId != null && option.checklistId == currentChecklistId) ||
      (option.jurisdictionId == itemJurisdictionId && option.lastRevision)
    );
  }

  clearChecklistCache(): void {
    this.checklistCacheService.clearCache();
  }

  updateItemField(item: any, field: string, value: any): void {
    if (field === 'time') {
      this.updateItemTimeField(item, value);
    } else {
      (item as any)[field] = value;

      if (field === 'platform') {
        item.platformId = value;
      }

      this.emitChanges();
    }
  }

  updateLoginData(value: any): void {
    this.getLoginDataInput().setValue(value);
    this.emitChanges();
  }

  updateNotes(value: any): void {
    this.getNotesInput().setValue(value);
    this.emitChanges();
  }

  emitChanges(): void {
    const cleanData = this.workingListItems.map(item => {
      const { nameControl, productControl, platformControl, checklistControl, timeControl, ...cleanItem } = item;
      
      return cleanItem;
    });
    
    this.onWorkingListUpdated.emit({
      type: 'workingList',
      data: {
        items: cleanData,
        loginData: this.getLoginDataInput().value,
        notes: this.getNotesInput().value
      }
    });
  }

 convertMinutesToHHMM(minutes: number | string | null | undefined): string {
    if (minutes === null || minutes === undefined || minutes === '' || minutes === '--:--') {
      return '--:--';
    }
    const numMinutes = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;
    if (isNaN(numMinutes) || numMinutes < 0) {
      return '--:--';
    }
    if (numMinutes === 0) {
      return '--:--';
    }
    
    const hours = Math.floor(numMinutes / 60);
    const mins = numMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }
  
  convertHHMMToMinutes(timeString: string | null | undefined): number {
    if (!timeString || timeString === '--:--') {
      return 0;
    }
    
    const timeRegex = /^(\d{1,2}):(\d{2})$/;
    const match = timeString.match(timeRegex);
    
    if (!match) {
      return 0;
    }
    
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    
    if (hours < 0 || minutes < 0 || minutes >= 60) {
      return 0;
    }
    return (hours * 60) + minutes;
  }
  
  updateItemTimeField(item: any, timeValue: string): void {
    const minutes = this.convertHHMMToMinutes(timeValue);      
    item.timeElapsed = minutes;
    this.emitChanges();
  }


  getFormControl(name: string): FormControl { 
    return this.form.get(name) as FormControl; 
  }
  
  getLoginDataInput(): FormControl { 
    return this.getFormControl('loginData'); 
  }
  
  getNotesInput(): FormControl { 
    return this.getFormControl('notes'); 
  }
  
  getWorkingItemNameInput(): FormControl { 
    return this.workingItemForm.get('name') as FormControl; 
  }
  
  getWorkingItemProductInput(): FormControl { 
    return this.workingItemForm.get('product') as FormControl; 
  }
  
  getWorkingItemPlatformInput(): FormControl { 
    return this.workingItemForm.get('platform') as FormControl; 
  }
  
  getWorkingItemChecklistInput(): FormControl { 
    return this.workingItemForm.get('checklist') as FormControl; 
  }
  
  getWorkingItemTimeInput(): FormControl { 
    return this.workingItemForm.get('time') as FormControl; 
  }
  
  getItemNameControl(item: any): FormControl {
    return item.nameControl || this.createFormControl(item, 'name');
  }
  
  getItemProductControl(item: any): FormControl {
    return item.productControl || this.createFormControl(item, 'product');
  }
  
  getItemPlatformControl(item: any): FormControl {
    return item.platformControl || this.createFormControl(item, 'platform');
  }
  
  getItemChecklistControl(item: any): FormControl {
    return item.checklistControl || this.createFormControl(item, 'checklist');
  }
  
  getItemTimeControl(item: any): FormControl {
    return item.timeControl || this.createFormControl(item, 'time');
  }

  isCreateChecklistEnabled(item: any): boolean {
    return item.isCreateChecklistEnabled !== false && !item.isCreatingChecklist;
  }

  getCreateChecklistButtonText(item: any): string {
    return item.isCreatingChecklist ? 'Creating...' : 'Create Checklist';
  }

  isItemValid(item: any): boolean {
    return this.getItemNameControl(item).valid &&
           this.getItemProductControl(item).valid &&
           this.getItemPlatformControl(item).valid &&
           this.getItemChecklistControl(item).valid;
  }
  
  isFormValid(): boolean {
    return this.workingListItems.every(item => this.isItemValid(item));
  }

  submitForm(): void {
    if (!this.projectId) {
      this.alertNotificationService.error('Project ID is missing');
      return;
    }
    this.workingListItems.forEach(item => {
      this.getItemNameControl(item).markAsTouched();
      this.getItemProductControl(item).markAsTouched();
      this.getItemPlatformControl(item).markAsTouched();
      this.getItemChecklistControl(item).markAsTouched();
    });

    if (!this.isFormValid()) {
      this.alertNotificationService.error('Please fill in all required fields correctly');
      return;
    }

    const updateRequests = this.workingListItems.map(item => {
      const workinglistId = item.projectWorkinglistId;
      
      if (!workinglistId) {
        return of({ success: false, item: item, error: 'Missing projectWorkinglistId' });
      }

      const itemData = {
        name: this.getItemNameControl(item).value || item.name,
        platformId: this.getItemPlatformControl(item).value ?? item.platform?.platformId ?? item.platform,
        checklistId: this.getItemChecklistControl(item).value ?? item.checklist?.checklistId ?? item.checklist,
        timeElapsed: this.convertHHMMToMinutes(this.getItemTimeControl(item).value) || item.timeElapsed || 0
      };

      return this.projectsService.updateProjectsWorkingLists(
        Number(this.projectId),
        workinglistId,
        itemData
      ).pipe(
        catchError(error => {
          console.error(`Error updating working list item ${workinglistId}:`, error);
          return of({ success: false, item: item, error: error });
        })
      );
    });

    forkJoin(updateRequests).subscribe({
      next: (results) => {
        const failedUpdates = results.filter((result: any) => result.success === false);
        
        if (failedUpdates.length === 0) {
          this.alertNotificationService.success('Working list updated successfully');
          this.loadWorkingListData(this.jurisdictionId);
        } else if (failedUpdates.length === results.length) {
          this.alertNotificationService.error('Failed to update all working list items');
        } else {
          this.alertNotificationService.warning(
            `${results.length - failedUpdates.length} items updated, ${failedUpdates.length} failed`
          );
          this.loadWorkingListData(this.jurisdictionId); 
        }
      },
      error: (error) => {
        console.error('Error during working list update:', error);
        this.alertNotificationService.error('An error occurred while updating the working list');
      }
    });
  }
}
